import { z } from "zod";

export type LoginInput = {
  email: string;
  password: string;
  testing: string;
};

export const EmailLoginParamSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export const PhoneLoginParamSchema = z.object({
  countryCode: z.string().min(1, "Country code is required"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\d{7,10}$/, "Invalid phone number please retry"),
  password: z.string().min(1, "Password is required"),
});

export const LoginParamSchema = z.union([
  EmailLoginParamSchema,
  PhoneLoginParamSchema,
]);

export const SendOtpParamSchema = z.object({
  countryCode: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  type: z.enum(["register", "forgot", "become-merchant"]),
  method: z.enum(["phone", "email"]),
});

export type User = {
  id: number;
  firstName: string;
  lastName: string;
  gender: string;
  email: string;
  countryCode: string;
  phone: string;
  avatar: string;
  birthday: string | null;
  nationalityId?: number | string;
};

type GetUserSuccess = {
  success: true;
  message: string;
  code?: number;
  user: User;
};

type GetUserError = {
  success: false;
  message: string;
};

export type UserResponse = GetUserSuccess | GetUserError;

export type LoginSuccess = {
  success: true;
  message: string;
  code?: number;
  user: User;
  cookieValue: string;
  sessionId?: string;
};

type LoginError = {
  success: false;
  message: string;
};

export type LoginResponse = LoginSuccess | LoginError;

export type SendOtpSuccess = {
  success: true;
  message: string;
  code?: string;
  data: Record<string, unknown>;
};

type SendOtpError = {
  success: false;
  message: string;
  code?: string;
};

export type SendOtpResponse = SendOtpSuccess | SendOtpError;

export const VerifyOtpParamSchema = z.object({
  otp: z.string().min(1, "OTP is required"),
  verificationToken: z.string().min(1, "Token is required"),
  method: z.enum(["email", "phone"]),
});

export type VerifyOtpSuccess = {
  success: true;
  message: string;
  code?: string;
  data: Record<string, unknown>;
};

type VerifyOtpError = {
  success: false;
  message: string;
  code?: string;
};

export type VerifyOtpResponse = VerifyOtpSuccess | VerifyOtpError;

const PasswordSchema = z
  .string()
  .min(1, "Password is required")
  .min(6, "Password must be at least 6 characters")
  .max(128, "Password must not exceed 128 characters");

export const PasswordFormSchema = z
  .object({
    password: PasswordSchema,
    confirmPassword: z.string().min(1, "Confirm password is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type PasswordFormFields = z.infer<typeof PasswordFormSchema>;

export const RegisterParamSchema = z.object({
  countryCode: z.string().min(1, "Country code is required"),
  phone: z.string().min(1, "Phone number is required"),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(125, "Password must not exceed 125 characters"),
  otpToken: z.string().min(1, "OTP Token is required"),
  fcmToken: z.string().optional(),
  deviceId: z.string().optional(),
  deviceUniqueId: z.string().optional(),
  model: z.string().optional(),
  systemVersion: z.string().optional(),
  isEmulator: z.number().optional(),
  lat: z.string().optional(),
  lng: z.string().optional(),
});

export type RegisterSuccess = {
  success: true;
  message: string;
  code?: number;
  user: User;
  cookieValue: string;
};

type RegisterError = {
  success: false;
  message: string;
  code?: string | number;
};

export type RegisterResponse = RegisterSuccess | RegisterError;

export const ResetPasswordParamSchema = z.object({
  token: z.string().min(1, "Token is required"),
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(125, "Password must not exceed 125 characters"),
  confirmPassword: z.string().min(1, "Confirm password is required"),
});

export type ResetPasswordSuccess = {
  success: true;
  message: string;
};

type ResetPasswordError = {
  success: false;
  message: string;
  code?: string;
};

export type ResetPasswordResponse = ResetPasswordSuccess | ResetPasswordError;
